import {
  ArrowTopRightOnSquareIcon,
  Button,
  CheckCircleIcon,
  Flex,
  Spinner,
  Text,
  XCircleIcon,
} from 'components'
import { useEffect, useState } from 'react'
import { forceLight } from 'utils'

export default function DiagnosticsPage() {
  const [checks, setChecks] = useState<DiagnosticCheck[]>([])
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null)
  const [isRunning, setIsRunning] = useState(false)

  useEffect(() => {
    collectDeviceInfo()
  }, [])

  function collectDeviceInfo() {
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection

    const info: DeviceInfo = {
      browser: navigator.userAgent.includes('Chrome')
        ? 'Chrome'
        : navigator.userAgent.includes('Firefox')
          ? 'Firefox'
          : navigator.userAgent.includes('Safari')
            ? 'Safari'
            : 'Unknown',
      browserVersion:
        navigator.userAgent.match(/(?:Chrome|Firefox|Safari)\/(\d+)/)?.[1] || 'Unknown',
      os: navigator.platform,
      resolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      connection: connection
        ? `${connection.effectiveType || 'unknown'} (${connection.downlink || 'unknown'} Mbps)`
        : 'Unknown',
      cookiesEnabled: navigator.cookieEnabled,
      localStorageEnabled: (() => {
        try {
          localStorage.setItem('test', 'test')
          localStorage.removeItem('test')
          return true
        } catch {
          return false
        }
      })(),
    }

    setDeviceInfo(info)
  }

  async function runDiagnostics() {
    setIsRunning(true)
    const results: DiagnosticCheck[] = diagnosticTests.map(test => ({
      name: test.name,
      status: 'loading' as const,
      url: test.url,
    }))
    setChecks(results)

    const testPromises = diagnosticTests.map(async (test, index) => {
      const startTime = performance.now()
      try {
        await test.test()
        const endTime = performance.now()
        setChecks(prev =>
          prev.map((check, checkIndex) =>
            checkIndex === index
              ? {
                  ...check,
                  status: 'success',
                  responseTime: Math.round(endTime - startTime),
                }
              : check
          )
        )
      } catch (error: any) {
        const endTime = performance.now()
        setChecks(prev =>
          prev.map((check, checkIndex) =>
            checkIndex === index
              ? {
                  ...check,
                  status: 'error',
                  error: error.message,
                  responseTime: Math.round(endTime - startTime),
                }
              : check
          )
        )
      }
    })

    await Promise.allSettled(testPromises)
    setIsRunning(false)
  }

  function shareReport() {
    let body = `Hello IT Team,\n\n`
    body += `I am experiencing some issues while using the Momentify application. Please find the diagnostics report below.\n\n`
    body += generateReport()
    body += `\n\nThank you for your help.\n\n`

    const subject = `Momentify Diagnostics Report - ${new Date().toISOString().split('T')[0]}`
    window.open(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      '_self'
    )
  }

  function generateReport(): string {
    const timestamp = new Date().toISOString()
    let report = `Momentify Diagnostics Report\n`
    report += `Generated: ${timestamp}\n\n`

    if (deviceInfo) {
      report += `DEVICE INFORMATION:\n`
      report += `- Browser: ${deviceInfo.browser} ${deviceInfo.browserVersion}\n`
      report += `- OS: ${deviceInfo.os}\n`
      report += `- Resolution: ${deviceInfo.resolution}\n`
      report += `- Timezone: ${deviceInfo.timezone}\n`
      report += `- Connection: ${deviceInfo.connection}\n`
      report += `- Cookies Enabled: ${deviceInfo.cookiesEnabled}\n`
      report += `- Local Storage Enabled: ${deviceInfo.localStorageEnabled}\n`
    }

    report += `\nDIAGNOSTIC RESULTS:\n`
    checks.forEach(check => {
      const status =
        check.status === 'success'
          ? '✅'
          : check.status === 'error'
            ? '❌'
            : check.status === 'loading'
              ? '⏳'
              : '⏸️'
      report += `- ${status} ${check.name}`
      report += ` (${check.responseTime}ms)`
      if (check.error) {
        report += ` - Error: ${check.error}`
        if (check.url) {
          report += ` (URL needs whitelisting: ${check.url})`
        }
      }
      report += `\n`
    })

    const currentUrl = window.location.origin + '/diagnostics'
    report += `\nDIAGNOSTICS PAGE: ${currentUrl}`
    report += `\nAPPLICATION INFORMATION: https://www.momentifyapp.com`
    return report
  }

  const allPassed = checks.length > 0 && checks.every(check => check.status === 'success')
  const anyFailed = checks.some(check => check.status === 'error')

  return (
    <Flex
      {...forceLight}
      backgroundColor="gray.50"
      overflow="auto"
      direction="column"
      p={12}
      flex={1}
    >
      <Flex maxWidth="800px" mx="auto" direction="column" gap={6}>
        <Flex direction="column" align="center" gap={10}>
          <img
            src="/common/momentify-logo-dark.svg"
            alt="Momentify Logo"
            style={{ height: '48px', width: 'auto' }}
          />
          <Flex direction="column" align="center" gap={2}>
            <Text textStyle="h3">System Diagnostics</Text>
            <Text textStyle="h6" color="gray.600" textAlign="center">
              Test connectivity and system status for Momentify
            </Text>
          </Flex>
        </Flex>

        <Flex direction="column" gap={6}>
          {deviceInfo && (
            <Flex
              direction="column"
              align="start"
              gap={3}
              p={4}
              bg="gray.50"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <Text textStyle="h4">Device & Connection Information</Text>
              <Flex direction="column" align="start" gap={1} fontSize="sm">
                <Text>
                  <strong>Browser:</strong> {deviceInfo.browser} {deviceInfo.browserVersion}
                </Text>
                <Text>
                  <strong>Operating System:</strong> {deviceInfo.os}
                </Text>
                <Text>
                  <strong>Screen Resolution:</strong> {deviceInfo.resolution}
                </Text>
                <Text>
                  <strong>Timezone:</strong> {deviceInfo.timezone}
                </Text>
                <Text>
                  <strong>Connection:</strong> {deviceInfo.connection}
                </Text>
                <Text>
                  <strong>Cookies:</strong> {deviceInfo.cookiesEnabled ? 'Enabled' : 'Disabled'}
                </Text>
                <Text>
                  <strong>Local Storage:</strong>{' '}
                  {deviceInfo.localStorageEnabled ? 'Enabled' : 'Disabled'}
                </Text>
              </Flex>
            </Flex>
          )}

          <Flex
            direction="column"
            align="start"
            gap={4}
            p={4}
            bg="white"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Flex justify="space-between" width="100%">
              <Text textStyle="h4">Diagnostic Tests</Text>
              <Button
                onClick={runDiagnostics}
                disabled={isRunning}
                leftIcon={isRunning ? <Spinner size="sm" /> : undefined}
                palette="primary"
              >
                {isRunning ? 'Running Tests...' : 'Run Diagnostics'}
              </Button>
            </Flex>

            {checks.length > 0 && (
              <Flex direction="column" align="start" gap={1}>
                {checks.map((check, index) => (
                  <Flex key={index} justify="space-between" p={2}>
                    <Flex gap={3}>
                      {check.status === 'loading' ? (
                        <Spinner size="sm" />
                      ) : check.status === 'success' ? (
                        <CheckCircleIcon color="green.500" />
                      ) : check.status === 'error' ? (
                        <XCircleIcon color="red.500" />
                      ) : null}
                      <Flex direction="column" align="start">
                        <Text>{check.name}</Text>
                        {check.error && (
                          <Flex direction="column">
                            <Text fontSize="sm" color="red.600" truncate>
                              {check.error}
                            </Text>
                            {check.url && (
                              <Text fontSize="xs" color="orange.600" truncate>
                                Whitelist: {check.url}
                              </Text>
                            )}
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            )}

            {checks.length > 0 && !isRunning && (
              <Flex
                direction="column"
                align="start"
                gap={2}
                width="100%"
                pt={4}
                borderTop="1px solid"
                borderColor="gray.200"
              >
                <Text textStyle="h5">
                  Status:{' '}
                  {allPassed ? (
                    <Text as="span" color="green.600">
                      All tests passed ✅
                    </Text>
                  ) : anyFailed ? (
                    <Text as="span" color="red.600">
                      Some tests failed ❌
                    </Text>
                  ) : (
                    <Text as="span" color="gray.600">
                      Tests in progress...
                    </Text>
                  )}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        {checks.length > 0 && !isRunning && (
          <Button
            variant="outline"
            onClick={shareReport}
            rightIcon={<ArrowTopRightOnSquareIcon fontSize="sm" />}
          >
            Share Report
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

interface DiagnosticCheck {
  name: string
  status: 'pending' | 'success' | 'error' | 'loading'
  error?: string
  responseTime?: number
  url: string
}

interface DeviceInfo {
  browser: string
  browserVersion: string
  os: string
  resolution: string
  timezone: string
  connection: string
  cookiesEnabled: boolean
  localStorageEnabled: boolean
}

const diagnosticTests = [
  {
    name: 'Authentication',
    url: 'https://identitytoolkit.googleapis.com',
    test: async () => {
      const { getAuth } = await import('firebase/auth')
      const { default: firebaseApp } = await import('utils/firebase')
      await getAuth(firebaseApp)
    },
  },
  {
    name: 'Database',
    url: 'https://firestore.googleapis.com',
    test: async () => {
      const { getFirestore, doc, getDoc } = await import('firebase/firestore')
      const { default: firebaseApp } = await import('utils/firebase')
      const db = getFirestore(firebaseApp)
      const testDoc = doc(db, 'globalConfiguration', 'test')
      await getDoc(testDoc)
    },
  },
  {
    name: 'Functions',
    url: 'https://cloudfunctions.googleapis.com',
    test: async () => {
      const { ping } = await import('utils/callables')
      await ping()
    },
  },
  {
    name: 'Storage',
    url: 'https://firebasestorage.googleapis.com',
    test: async () => {
      const { getStorage, ref, listAll } = await import('firebase/storage')
      const { default: firebaseApp } = await import('utils/firebase')
      const storage = getStorage(firebaseApp)
      const googleFontsRef = ref(storage, 'diagnostics')
      await listAll(googleFontsRef)
    },
  },
]
